import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { workspaceLink } from "./dealDesk/workspaceLinks";
import DealDeskToolbarContext from './dealDesk/DealDeskToolbarContext';
import PlanStudioToolbar from './dealDesk/PlanStudioToolbar';
import { importWordDocument } from '../shared/wordDocumentImport';

export const PLAN_RECOVERY_TIMEOUT = 4000;
const expiredMessage = "Your session ended. This draft cannot access Salesforce.";

const embeddedStyles = `
  body.deal-desk-embedded .app { --header: 0px; grid-template-rows: minmax(0, 1fr); }
  body.deal-desk-embedded .app .topbar { display: none !important; }
  body.deal-desk-embedded #popover.plan-host-popover { left: auto !important; right: 10px !important; top: 10px !important; max-height: calc(100dvh - 20px); overflow-y: auto; }
`;

// The document has an opaque origin. Cookies and API access stay in this parent.
const RiomaxPlanStudio = forwardRef(function RiomaxPlanStudio({ baseUrl = "", onClose, embedded = false, active: appActive = true, focusTarget }, ref) {
  const frame = useRef(null);
  const close = useRef(onClose);
  const control = useRef(null);
  const focus = useRef(focusTarget); focus.current = focusTarget;
  const revokedLifetime = useRef(false);
  const recoverySequence = useRef(0);
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");
  const toolsTarget = useContext(DealDeskToolbarContext);
  const [toolbarStatus, setToolbarStatus] = useState({ ready: false, status: 'Loading...', error: '' });
  const [quickDraft, setQuickDraft] = useState('');
  const toolbarSequence = useRef(0), draftSequence = useRef(0);
  useEffect(() => {
    if (!embedded || !appActive) return undefined;
    const shortcut = event => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && !document.querySelector('dialog[open]') && toolbarStatus.ready) {
        event.preventDefault(); event.stopPropagation();
        control.current?.toolbar('find').catch(() => {});
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [embedded, appActive, toolbarStatus.ready]);
  close.current = onClose;
  useEffect(() => { control.current?.focus(); }, [focusTarget]);
  useImperativeHandle(ref, () => ({
    recover: () => control.current?.recover() || Promise.resolve(null),
    discard: () => control.current?.discard(),
  }), []);
  const frameHtml = useMemo(() => {
    if (!html || !embedded) return html;
    // Adapt only the host chrome; keep the protected source and its event handlers intact.
    const document = new DOMParser().parseFromString(html, "text/html");
    document.body.classList.add("deal-desk-embedded");
    const style = document.createElement("style");
    style.dataset.dealDesk = "embedded";
    style.textContent = embeddedStyles;
    document.head.appendChild(style);
    return `<!doctype html>${document.documentElement.outerHTML}`;
  }, [html, embedded]);

  useEffect(() => {
    const abort = new AbortController();
    let active = true;
    let revoked = revokedLifetime.current;
    let recovery = null;
    let ready = false, focused = null, wordImporting = false;
    const sendFocus = () => {
      const target = focus.current;
      if (!active || revoked || !ready || !target || focused === target.requestId) return;
      focused = target.requestId;
      frame.current?.contentWindow?.postMessage({ type: "plan:focus-comment", target }, "*");
    };
    const pending = new Map(), commands = new Map();
    const cancelCommands = () => { for (const command of commands.values()) { clearTimeout(command.timer); command.reject(new Error('The planning session is no longer available.')); } commands.clear(); };
    const expired = () => Object.assign(new Error(expiredMessage), { status: 401 });
    const revoke = () => {
      revokedLifetime.current = true;
      revoked = true;
      abort.abort();
      for (const send of pending.values()) send({ error: expiredMessage, status: 401 });
      pending.clear();
      cancelCommands();
      setToolbarStatus({ ready: false, status: 'Session ended', error: expiredMessage });
    };
    const dispose = () => {
      active = false;
      abort.abort();
      pending.clear();
      cancelCommands();
      if (recovery) {
        clearTimeout(recovery.timer);
        recovery.reject(new Error("Draft recovery was cleared."));
        recovery = null;
      }
    };
    control.current = {
      focus: sendFocus,
      draft(value) {
        if (!active || revoked || !ready) return;
        frame.current?.contentWindow?.postMessage({ type: 'plan:toolbar-draft', value, version: ++draftSequence.current }, '*');
      },
      toolbar(action) {
        if (!active || revoked || !ready) return Promise.reject(new Error('Wait for Idea Builder to finish opening.'));
        if (!['add', 'new', 'find', 'layout', 'guide', 'export', 'save', 'refresh', 'navigation'].includes(action)) return Promise.reject(new Error('Unsupported planning action.'));
        const id = `toolbar-${++toolbarSequence.current}`;
        return new Promise((resolve, reject) => {
          const timer = setTimeout(() => { commands.delete(id); reject(new Error('The action was not confirmed. Check the plan before trying again.')); }, 75000);
          commands.set(id, { resolve, reject, timer });
          frame.current?.focus();
          frame.current?.contentWindow?.postMessage({ type: 'plan:toolbar-command', id, action }, '*');
        });
      },
      discard() { revoke(); dispose(); },
      recover() {
        revoke();
        if (!active || !frame.current?.contentWindow) return Promise.resolve(null);
        if (recovery) return recovery.promise;
        const id = `recovery-${++recoverySequence.current}`;
        let resolve, reject;
        const promise = new Promise((done, fail) => { resolve = done; reject = fail; });
        const timer = setTimeout(() => {
          recovery = null;
          reject(new Error("Draft capture did not finish. Your draft is still open; retry before continuing."));
        }, PLAN_RECOVERY_TIMEOUT);
        recovery = { id, promise, resolve, reject, timer };
        frame.current.contentWindow.postMessage({ type: "plan:recovery-request", id }, "*");
        return promise;
      },
    };
    const api = async (path, options = {}, binary = false) => {
      if (!active || revoked) throw expired();
      const response = await fetch(`${baseUrl}/backend/sow/plan-studio${path}`, {
        ...options, signal: abort.signal, credentials: "include", cache: "no-store",
        headers: { "X-SOW-Project": "riomax", ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }) },
      });
      if (!active || revoked) throw expired();
      if (response.status === 401) {
        revoke();
        window.dispatchEvent(new CustomEvent("sow:session-expired"));
        throw expired();
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const message = Array.isArray(payload.message) ? payload.message.join(" ") : payload.message;
        throw Object.assign(new Error(message || (response.status === 413
          ? "The server rejected the file size. Try a smaller file; attachments support up to 10 MB."
          : "Plan Studio could not complete this request.")), { status: response.status });
      }
      const result = binary ? await response.blob() : path === "/content" ? await response.text() : (await response.json()).data;
      if (!active || revoked) throw expired();
      return result;
    };
    const upload = (path, body, progress) => new Promise((resolve, reject) => {
      if (!active || revoked) { reject(expired()); return; }
      const xhr = new XMLHttpRequest();
      let finished = false;
      const finish = (failure, data) => {
        if (finished) return;
        finished = true;
        abort.signal.removeEventListener("abort", cancel);
        failure ? reject(failure) : resolve(data);
      };
      const cancel = () => { xhr.abort(); finish(expired()); };
      xhr.open("POST", `${baseUrl}/backend/sow/plan-studio${path}`);
      xhr.withCredentials = true;
      xhr.timeout = 70000;
      xhr.setRequestHeader("X-SOW-Project", "riomax");
      xhr.upload.onprogress = event => {
        if (active && !revoked && !finished && event.lengthComputable && event.total > 0) {
          progress(Math.max(0, Math.min(100, Math.floor(event.loaded / event.total * 100))));
        }
      };
      xhr.onload = () => {
        if (!active || revoked) { finish(expired()); return; }
        if (xhr.status === 401) {
          finish(expired()); revoke();
          window.dispatchEvent(new CustomEvent("sow:session-expired"));
          return;
        }
        let payload;
        try { payload = JSON.parse(xhr.responseText); } catch { payload = {}; }
        if (xhr.status < 200 || xhr.status >= 300) {
          const message = Array.isArray(payload.message) ? payload.message.join(" ") : payload.message;
          finish(Object.assign(new Error(message || (xhr.status === 413
            ? "The server rejected the file size. Try a smaller file; attachments support up to 10 MB."
            : "The file could not be uploaded. Keep the original file and retry.")), { status: xhr.status, data: payload.data }));
        } else if (!payload.data) finish(new Error("The server did not confirm this file. Keep the original file before retrying."));
        else finish(null, payload.data);
      };
      xhr.onerror = () => finish(new Error("Upload interrupted. Keep the original file and retry when your connection is available."));
      xhr.ontimeout = () => finish(new Error("Upload confirmation timed out. Check the idea's files before retrying."));
      xhr.onabort = () => finish(expired());
      abort.signal.addEventListener("abort", cancel, { once: true });
      try { xhr.send(body); } catch (failure) { finish(failure); }
    });
    const receive = async (event) => {
      if (!active || event.source !== frame.current?.contentWindow || event.origin !== "null") return;
      const message = event.data;
      if (message?.type === 'plan:toolbar-state') {
        const data = message.data;
        if (!revoked && data && typeof data.ready === 'boolean' && ['Saved', 'Save', 'Saving...', 'Not saved'].includes(data.status) && typeof data.error === 'string' && data.error.length <= 2000) {
          setToolbarStatus({ ready: data.ready, status: data.status, error: data.error });
          if (data.draftVersion === draftSequence.current && typeof data.draft === 'string' && data.draft.length <= 180) setQuickDraft(data.draft);
        }
        return;
      }
      if (message?.type === 'plan:toolbar-result') {
        const command = commands.get(message.id);
        if (command) { commands.delete(message.id); clearTimeout(command.timer); message.error ? command.reject(new Error(String(message.error).slice(0, 2000))) : command.resolve(); }
        return;
      }
      if (message?.type === "plan:ready") { ready = true; sendFocus(); return; }
      if (message?.type === "plan:recovery" && recovery && message.id === recovery.id) {
        const current = recovery;
        clearTimeout(current.timer);
        recovery = null;
        try {
          if (message.error) throw new Error("Draft capture failed. Your draft is still open; retry before continuing.");
          const data = message.data;
          if (data === null) { current.resolve(null); return; }
          if (!data || !Number.isSafeInteger(data.revision) || data.revision < 0 || !Array.isArray(data.workspace?.ideas)) {
            throw new Error("The draft could not be verified. Retry before continuing.");
          }
          // Only export the planning snapshot, never session state or arbitrary message fields.
          const json = JSON.stringify({ format: "riomax-plan-recovery-v1", revision: data.revision,
            workspace: data.workspace, drafts: data.drafts || {}, saved: false });
          if (new Blob([json]).size > 8 * 1024 * 1024) throw new Error("The draft is too large to capture. Keep this page open.");
          current.resolve(json);
        } catch (failure) { current.reject(failure); }
        return;
      }
      if (message?.type !== "plan:request" || typeof message.id !== "string" || message.id.length > 100 || pending.has(message.id)) return;
      const source = event.source;
      const send = (data) => {
        if (active && frame.current?.contentWindow === source) source.postMessage({ type: "plan:response", id: message.id, ...data }, "*");
      };
      if (revoked) { send({ error: expiredMessage, status: 401 }); return; }
      pending.set(message.id, send);
      try {
        let data;
        switch (message.operation) {
          case 'import-word': {
            const { file, name } = message.payload || {};
            if (!(file instanceof Blob) || !/\.docx$/i.test(String(name || '')) || !file.size || file.size > 10 * 1024 * 1024) {
              throw new Error('Choose a Word .docx document under 10 MB.');
            }
            if (wordImporting) throw new Error('Wait for the current Word document to finish opening.');
            wordImporting = true;
            try { data = await importWordDocument(new File([file], name, { type: file.type })); }
            finally { wordImporting = false; }
            if (!active || revoked) throw expired();
            break;
          }
          case "copy-link": {
            const url = workspaceLink('idea', message.payload?.ideaId);
            let copied = false;
            try { await navigator.clipboard.writeText(url); copied = true; } catch { /* The frame offers a selectable link when clipboard access is denied. */ }
            data = { url, copied }; break;
          }
          case "load": data = await api(""); break;
          case "save": data = await api("", { method: "PUT", body: JSON.stringify(message.payload) }); break;
          case 'inline-image': {
            const { ideaId, contentDocumentId } = message.payload || {};
            if ((ideaId && !/^[A-Za-z0-9_.:-]{1,120}$/.test(ideaId)) || !/^069[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/.test(contentDocumentId || '')) throw new Error('Invalid pasted image identity.');
            data = await api(ideaId ? `/${encodeURIComponent(ideaId)}/files/${encodeURIComponent(contentDocumentId)}/inline-image`
              : `/inline-images/${encodeURIComponent(contentDocumentId)}/inline-image`, { method: 'POST' });
            break;
          }
          case 'upload-inline-draft': {
            const { file, name, fileId } = message.payload || {};
            if (!(file instanceof Blob) || !file.size || file.size > 10 * 1024 * 1024
              || !['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) throw new Error('Choose a PNG, JPEG, GIF or WebP image under 10 MB.');
            const body = new FormData(); body.append('file', file, String(name || 'pasted-image.png'));
            data = await upload('/inline-images', body, percent => {
              if (active && !revoked && pending.has(message.id) && frame.current?.contentWindow === source) {
                source.postMessage({ type: 'plan:upload-progress', id: message.id, percent }, '*');
              }
            });
            data = { ...data, id: fileId };
            break;
          }
          case "upload": {
            const { ideaId, fileId, file, name } = message.payload || {};
            if (!(file instanceof Blob) || file.size > 10 * 1024 * 1024) throw new Error("Choose a file under 10 MB.");
            const body = new FormData(); body.append("file", file, String(name || "attachment"));
            data = await upload(`/${encodeURIComponent(ideaId)}/files/${encodeURIComponent(fileId)}`, body, percent => {
              if (active && !revoked && pending.has(message.id) && frame.current?.contentWindow === source) {
                source.postMessage({ type: "plan:upload-progress", id: message.id, percent }, "*");
              }
            });
            break;
          }
          case "download": {
            const { ideaId, documentId } = message.payload || {};
            data = await api(`/${encodeURIComponent(ideaId)}/files/${encodeURIComponent(documentId)}/download`, {}, true);
            break;
          }
          case "close": close.current(); return;
          default: throw new Error("Unsupported planning request.");
        }
        if (pending.has(message.id)) send({ data });
      } catch (failure) {
        if (pending.has(message.id)) send({ error: failure.message, status: failure.status || 500,
          ...(failure.data?.staged ? { stagedFile: failure.data } : {}) });
      } finally { pending.delete(message.id); }
    };
    window.addEventListener("message", receive);
    api("/content").then((value) => { if (active && !revoked) setHtml(value); }).catch((failure) => { if (active) setError(failure.message); });
    return () => { dispose(); control.current = null; window.removeEventListener("message", receive); };
  }, [baseUrl]);

  return <section className="sow-plan-studio" aria-label="Riomax Plan Studio">
    {embedded && appActive && toolsTarget && createPortal(<PlanStudioToolbar status={toolbarStatus} draft={quickDraft}
      onDraft={value => { setQuickDraft(value); control.current?.draft(value); }}
      onAction={action => control.current?.toolbar(action) || Promise.reject(new Error('Idea Builder is not ready.'))} />, toolsTarget)}
    {html ? <iframe ref={frame} title="Riomax Plan Studio" srcDoc={frameHtml}
      sandbox="allow-scripts allow-forms allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox" />
      : <div role={error ? "alert" : "status"} className="sow-plan-studio-loading">
        <p>{error || "Opening Plan Studio..."}</p>
        {error && <button type="button" onClick={onClose}>Back to workspace</button>}
      </div>}
  </section>;
});

export default RiomaxPlanStudio;
